import { Layout } from "@/components/layout/layout";
import { useParams, Link } from "wouter";
import {
  useGetPublicPlayerProfile,
  getGetPublicPlayerProfileQueryKey,
  useGetPlayerHeadToHead,
  getGetPlayerHeadToHeadQueryKey,
  useGetPlayerAttendance,
  getGetPlayerAttendanceQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AttendanceHeatmap } from "@/components/attendance-heatmap";
import { Loader2, Trophy, Calendar, DollarSign, Target, MapPin, History, Flame, Swords } from "lucide-react";
import { formatDateTime, formatMoney } from "@/lib/format";
import NotFound from "@/pages/not-found";
import { useCurrentPlayer } from "@/hooks/useCurrentPlayer";

export default function PublicPlayerProfile() {
  const { id } = useParams<{ id: string }>();
  const playerId = parseInt(id ?? "", 10);
  const { player: viewer, isLoggedIn } = useCurrentPlayer();
  const { data, isLoading, isError } = useGetPublicPlayerProfile(playerId, {
    query: {
      queryKey: getGetPublicPlayerProfileQueryKey(playerId),
      enabled: !isNaN(playerId),
      retry: false,
      throwOnError: false,
    },
  });
  const showH2h = isLoggedIn && !!viewer && !isNaN(playerId) && viewer.id !== playerId;
  const { data: attendance } = useGetPlayerAttendance(playerId, undefined, {
    query: {
      queryKey: getGetPlayerAttendanceQueryKey(playerId),
      enabled: !isNaN(playerId) && !!data && data.publicAttendance,
      retry: false,
      throwOnError: false,
    },
  });
  const { data: h2h } = useGetPlayerHeadToHead(playerId, {
    query: {
      queryKey: getGetPlayerHeadToHeadQueryKey(playerId),
      enabled: showH2h,
      retry: false,
      throwOnError: false,
    },
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-24 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (isError || !data) {
    return <NotFound />;
  }

  const memberSince = new Date(data.memberSince).getFullYear();

  return (
    <Layout>
      <div className="bg-card border-b border-border py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 text-center sm:text-left">
            <div
              className="h-24 w-24 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-bold text-4xl border-2 border-secondary/30"
              data-testid="public-profile-avatar"
            >
              {data.name.charAt(0)}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground" data-testid="public-profile-name">
                {data.name}
              </h1>
              <p className="text-muted-foreground mt-1">
                Member since {memberSince}
                {data.homeVenue ? (
                  <>
                    {" "}• <MapPin className="inline h-3 w-3 mb-0.5" /> {data.homeVenue}
                  </>
                ) : null}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-5xl space-y-8">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-4" data-testid="season-stats-heading">
            {data.currentSeason ? `${data.currentSeason} Season Stats` : "Season Stats"}
          </h2>
          <div className="grid gap-4 grid-cols-2 md:grid-cols-5">
          <StatCard
            icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
            label="Tournaments"
            value={String(data.totalTournaments)}
            testid="stat-tournaments"
          />
          <StatCard
            icon={<Target className="h-4 w-4 text-muted-foreground" />}
            label="Total Points"
            value={String(data.totalPoints)}
            testid="stat-points"
          />
          <StatCard
            icon={<Trophy className="h-4 w-4 text-muted-foreground" />}
            label="Wins"
            value={String(data.wins)}
            testid="stat-wins"
          />
          <StatCard
            icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
            label="Winnings"
            value={formatMoney(data.totalWinnings)}
            testid="stat-winnings"
          />
          <StatCard
            icon={<Flame className={`h-4 w-4 ${data.currentWinStreak > 0 ? "text-orange-500" : "text-muted-foreground"}`} />}
            label="Win Streak"
            value={data.currentWinStreak > 0 ? `${data.currentWinStreak} 🔥` : data.longestWinStreak > 0 ? `Best ${data.longestWinStreak}` : "—"}
            testid="stat-streak"
          />
          </div>
        </div>

        {showH2h && h2h && h2h.meetings > 0 ? (
          <Card className="bg-background border-border shadow-md" data-testid="card-h2h">
            <CardHeader className="border-b border-border pb-6">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Swords className="h-6 w-6 text-primary" /> Head-to-Head vs You
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">You beat them</p>
                  <p className="text-3xl font-bold text-emerald-500" data-testid="h2h-losses">{h2h.losses}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">They beat you</p>
                  <p className="text-3xl font-bold text-destructive" data-testid="h2h-wins">{h2h.wins}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Ties</p>
                  <p className="text-3xl font-bold text-muted-foreground" data-testid="h2h-ties">{h2h.ties}</p>
                </div>
              </div>
              {h2h.recent.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-foreground">Recent meetings</p>
                  {h2h.recent.map((m) => {
                    const youWon = m.viewerPosition < m.playerPosition;
                    const tied = m.viewerPosition === m.playerPosition;
                    return (
                      <div
                        key={m.tournamentId}
                        className="flex items-center justify-between p-3 rounded-lg border border-border bg-card/50 text-sm"
                        data-testid={`h2h-meeting-${m.tournamentId}`}
                      >
                        <div className="min-w-0 flex-1">
                          <Link href={`/tournaments/${m.tournamentId}`} className="font-medium text-foreground hover:text-primary truncate block">
                            {m.tournamentName}
                          </Link>
                          <div className="text-xs text-muted-foreground">{formatDateTime(m.date)}</div>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <span className="text-muted-foreground">You #{m.viewerPosition}</span>{" "}
                          <span className="text-muted-foreground">·</span>{" "}
                          <span className="text-muted-foreground">Them #{m.playerPosition}</span>
                          <div className={`text-xs font-bold ${tied ? "text-muted-foreground" : youWon ? "text-emerald-500" : "text-destructive"}`}>
                            {tied ? "Tie" : youWon ? "You won" : "They won"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {data.publicAttendance ? (
          <Card className="bg-background border-border shadow-md" data-testid="card-attendance">
            <CardHeader className="border-b border-border pb-6">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Calendar className="h-6 w-6 text-primary" /> Attendance (last 12 months)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <AttendanceHeatmap
                cells={(attendance ?? []).map((d) => ({
                  date: d.date,
                  count: d.tournamentCount,
                  href: `/tournaments/${d.tournamentId}`,
                  tooltip: `${d.date}: ${d.tournamentName} (${d.leagueName})${d.tournamentCount > 1 ? ` +${d.tournamentCount - 1} more` : ""}`,
                }))}
                emptyLabel="No tournaments attended in the last 12 months."
              />
            </CardContent>
          </Card>
        ) : null}

        <Card className="bg-background border-border shadow-md">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              <History className="h-6 w-6 text-primary" /> Recent Finishes
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {data.recentFinishes.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-lg font-medium text-foreground">No tournaments yet</p>
                <p className="text-muted-foreground mt-2">Their first cash will show up here.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="recent-finishes">
                {data.recentFinishes.map((entry, i) => (
                  <div
                    key={i}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border border-border bg-card/50 hover:bg-card transition-colors gap-4"
                  >
                    <div>
                      <Link
                        href={`/tournaments/${entry.tournamentId}`}
                        className="font-bold text-lg hover:text-primary transition-colors"
                      >
                        {entry.tournamentName}
                      </Link>
                      <div className="text-sm text-muted-foreground mt-1">
                        {formatDateTime(entry.date)} • {entry.leagueName}
                      </div>
                    </div>
                    <div className="flex items-center gap-8 sm:gap-12 pt-2 sm:pt-0 border-t border-border sm:border-0">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Pos</p>
                        <p className="font-bold text-foreground">
                          {entry.position > 0 ? (
                            <span className={entry.position <= 3 ? "text-primary" : ""}>
                              #{entry.position}
                            </span>
                          ) : (
                            "-"
                          )}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Pts</p>
                        <p className="font-bold text-primary">{entry.points || "-"}</p>
                      </div>
                      <div className="text-center w-24">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Won</p>
                        <p className="font-bold text-green-500">
                          {entry.winnings > 0 ? formatMoney(entry.winnings) : "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function StatCard({
  icon,
  label,
  value,
  testid,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  testid: string;
}) {
  return (
    <Card className="bg-card/50 backdrop-blur border-border/50 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-foreground" data-testid={testid}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
